import type { StateCreator } from 'zustand'

import type { ThemePreference } from '@modules/settings'
import { readErrorDescription } from '@modules/settings/utils/readErrorDescription'
import { resolveThemePreference } from '@modules/settings/utils/resolveThemePreference'
import { getUserRegionalData, updateUserAvatar, updateUserEmail, updateUserNames } from '@modules/user/regional-user'
import { deleteUser } from '@modules/user/user'

import type { AccountStore } from './account.type'

type AccountSet = Parameters<StateCreator<AccountStore, [['zustand/immer', never]], [], AccountStore>>[0]

export const createAccountActions = (
  set: AccountSet
): Omit<AccountStore, 'profile' | 'signInMethods' | 'twoFactorStatus' | 'themePreference' | 'loading' | 'error'> => ({
  getAccountProfile: async () => {
    set((state: AccountStore) => {
      state.loading = true
      state.error = null
    })
    try {
      const user = await getUserRegionalData()
      if (!user) throw new Error('Failed to fetch account data')
      set((state: AccountStore) => {
        state.profile = {
          id: user.userId,
          firstName: user.firstname,
          lastName: user.lastname,
          email: user.userEmail,
          picture: user.avatarUrl ?? null,
          createdAt: user.createdAt ?? '',
        }
        state.signInMethods = []
        state.twoFactorStatus = { enabled: false, method: null }
      })
    } catch (err) {
      set((state: AccountStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to fetch account data'
      })
    } finally {
      set((state: AccountStore) => {
        state.loading = false
      })
    }
  },
  updateFullName: async (firstName: string, lastName: string) => {
    set((state: AccountStore) => {
      state.loading = true
      state.error = null
    })
    try {
      await updateUserNames(firstName, lastName)
      set((state: AccountStore) => {
        if (state.profile) {
          state.profile.firstName = firstName
          state.profile.lastName = lastName
        }
      })
      return true
    } catch (err) {
      set((state: AccountStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to update name'
      })
      return false
    } finally {
      set((state: AccountStore) => {
        state.loading = false
      })
    }
  },
  updateEmail: async (email: string) => {
    set((state: AccountStore) => {
      state.loading = true
      state.error = null
    })
    try {
      await updateUserEmail(email)
      set((state: AccountStore) => {
        if (state.profile) {
          state.profile.email = email
        }
      })
      return true
    } catch (err) {
      set((state: AccountStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to update email'
      })
      return false
    } finally {
      set((state: AccountStore) => {
        state.loading = false
      })
    }
  },
  updateProfilePicture: async (picture: number[]) => {
    set((state: AccountStore) => {
      state.loading = true
      state.error = null
    })
    try {
      await updateUserAvatar(picture)
      const user = await getUserRegionalData()
      if (!user) throw new Error('Failed to refresh profile after avatar update')
      set((state: AccountStore) => {
        state.profile = {
          id: user.userId,
          firstName: user.firstname,
          lastName: user.lastname,
          email: user.userEmail,
          picture: user.avatarUrl ?? null,
          createdAt: user.createdAt ?? '',
        }
        state.signInMethods = []
        state.twoFactorStatus = { enabled: false, method: null }
      })
      return true
    } catch (err) {
      set((state: AccountStore) => {
        state.error = readErrorDescription(err, 'Failed to update profile picture')
      })
      return false
    } finally {
      set((state: AccountStore) => {
        state.loading = false
      })
    }
  },
  updatePassword: async () => {
    set((state: AccountStore) => {
      state.error = 'Not implemented'
    })
  },
  unlinkProvider: async () => {
    set((state: AccountStore) => {
      state.error = 'Not implemented'
    })
  },
  configureTwoFactor: async () => {
    set((state: AccountStore) => {
      state.error = 'Not implemented'
    })
  },
  setThemePreference: (theme: ThemePreference) => {
    resolveThemePreference.save(theme)
    set((state: AccountStore) => {
      state.themePreference = theme
    })
  },
  deleteAccount: async () => {
    set((state: AccountStore) => {
      state.loading = true
      state.error = null
    })
    try {
      await deleteUser()
      return true
    } catch (err) {
      set((state: AccountStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to delete account'
      })
      return false
    } finally {
      set((state: AccountStore) => {
        state.loading = false
      })
    }
  },
  clearError: () => {
    set((state: AccountStore) => {
      state.error = null
    })
  },
})
